import { useState } from 'react';
import {
  createFrontendPlugin,
  PageBlueprint,
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/frontend-plugin-api';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { InfoCard, WarningPanel, Header, Page, Content } from '@backstage/core-components';
import { TextField, Button, Grid, CircularProgress, Link } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import WebIcon from '@material-ui/icons/Web';
import { staticSitePermission } from './conduitPermissions';

function StaticSiteForm() {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [form, setForm] = useState({
    product_name: '',
    product_slug: '',
    domain_name: '',
    embed_url: '',
    page_title: '',
    redirect_root_to_path: '',
    prod_url: '',
    jira_ticket: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    runId: string; runUrl: string; status: string; workspaceName: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const set = (field: string, value: string) =>
    setForm(previous => ({ ...previous, [field]: value }));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setResult(null);

    const missingFields = [
      ['Site name', form.product_name],
      ['Short site ID', form.product_slug],
      ['Sandbox web address', form.domain_name],
      ['Browser-tab title', form.page_title],
      ['Final Young Life web address', form.prod_url],
      ['Jira ticket', form.jira_ticket],
      ['Report or page link', form.embed_url],
    ].filter(([, value]) => !value.trim()).map(([label]) => label);

    if (missingFields.length) {
      setError(`Please complete these fields: ${missingFields.join(', ')}. Gray example text is not an entered value.`);
      return;
    }

    setSubmitting(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('conduit-static-site');
      const response = await fetchApi.fetch(`${baseUrl}/provision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          redirect_root_to_path: form.redirect_root_to_path || null,
        }),
      });
      const data = await response.json();
      if (!response.ok) setError(data.error || `Request failed (${response.status})`);
      else setResult(data);
    } catch (requestError: any) {
      setError(requestError.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const fields = [
    {
      key: 'product_name',
      label: 'What should this site be called?',
      placeholder: 'BI Team Test Site',
      helperText: 'Use a name your team will recognize. This is the display name for the site.',
    },
    {
      key: 'product_slug',
      label: 'Choose a short site ID',
      placeholder: 'bi-team-test',
      helperText: 'Use lowercase letters, numbers, and hyphens only. We use this ID to name the supporting resources.',
    },
    {
      key: 'domain_name',
      label: 'What sandbox web address do you want?',
      placeholder: 'bi-team-test.ylsandbox.dev',
      helperText: 'This is the test address people will visit. It must end in .ylsandbox.dev.',
    },
    {
      key: 'page_title',
      label: 'What should appear in the browser tab?',
      placeholder: 'BI Team Test Site',
      helperText: 'This title appears at the top of the browser window or tab.',
    },
    {
      key: 'prod_url',
      label: 'What will the final Young Life web address be?',
      placeholder: 'bi-team-test.younglife.org/report',
      helperText: 'Enter the planned production address. Creating this sandbox site does not make the production address live.',
    },
    {
      key: 'jira_ticket',
      label: 'What Jira ticket tracks this request?',
      placeholder: 'INFRA-1165',
      helperText: 'Enter the ticket number so the site can be traced back to the approved work.',
    },
    {
      key: 'redirect_root_to_path',
      label: 'Should the home page open a specific path? (optional)',
      placeholder: '/report',
      helperText: 'Leave this blank to use the home page. Enter a path such as /report to send visitors there automatically.',
    },
  ];

  return (
    <Page themeId="tool">
      <Header title="Create a Report Site" subtitle="Publish a report or other web content at a Young Life sandbox address" />
      <Content>
        <RequirePermission permission={staticSitePermission} errorPage={
          <WarningPanel title="Access Denied" severity="error">
            This function requires the <strong>Platform Engineer</strong> or <strong>Platform Admin</strong> role.
          </WarningPanel>
        }>
          <InfoCard title="Tell us about the site">
            <form onSubmit={submit} noValidate>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Alert severity="info">
                    Use this form to create a test site. You will need a link to the report or page you want to display and a Jira ticket for the work.
                  </Alert>
                </Grid>
                {fields.map(({ key, label, placeholder, helperText }) => (
                  <Grid item xs={12} md={6} key={key}>
                    <TextField label={label} placeholder={placeholder} value={form[key as keyof typeof form]}
                      helperText={helperText} onChange={event => set(key, event.target.value)}
                      required={key !== 'redirect_root_to_path'} fullWidth />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <TextField label="Paste the link to the report or page you want to display"
                    placeholder="https://app.powerbi.com/reportEmbed?..." value={form.embed_url}
                    helperText="For Power BI, use the secure embed link supplied by the BI team. The link must begin with https://."
                    onChange={event => set('embed_url', event.target.value)} required fullWidth multiline minRows={2} />
                </Grid>
                <Grid item xs={12}>
                  <Button type="submit" variant="contained" color="primary" disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={18} /> : <WebIcon />}>
                    {submitting ? 'Creating your test site...' : 'Create Test Site'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </InfoCard>
          {result && <Alert severity="success" style={{ marginTop: 16 }}>
            Your request was submitted successfully. The site is now being prepared.{' '}
            <Link href={result.runUrl} target="_blank" rel="noopener">Follow its progress</Link>
          </Alert>}
          {error && <Alert severity="error" onClose={() => setError(null)} style={{ marginTop: 16 }}>
            <strong>We could not submit the request.</strong> {error}
          </Alert>}
        </RequirePermission>
      </Content>
    </Page>
  );
}

export default createFrontendPlugin({
  pluginId: 'conduit-static-site',
  extensions: [PageBlueprint.make({
    params: { path: '/static-site', title: 'Static Site', icon: <WebIcon />, loader: async () => <StaticSiteForm /> },
  })],
});
